import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Lock } from 'lucide-react';

const LOGO = '/assets/kuutio-logo.svg';
const BLUE = '#5B9DD9';

const unfold = keyframes`
  from { opacity: 0; transform: translateY(-6px) scale(0.96); }
  to { opacity: 1; transform: none; }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
`;

const Anchor = styled.div`
  position: relative;
  flex-shrink: 0;
`;

/* The Kuutio logo is the menu's button: a quarter turn, like a cube, while it is open */
const LogoButton = styled.button`
  width: 44px;
  height: 44px;
  padding: 2px;
  margin: -2px;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  display: grid;
  place-items: center;
  -webkit-tap-highlight-color: transparent;
  &:disabled { cursor: default; }
  &:focus-visible { outline: 2px solid ${BLUE}; outline-offset: 2px; }

  img {
    width: 40px;
    height: 40px;
    transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1), filter 320ms ease;
    transform: rotate(${(p) => (p.$open ? '90deg' : '0deg')});
    filter: ${(p) => (p.$open ? `drop-shadow(0 0 8px rgba(91, 157, 217, 0.6))` : 'none')};
  }
  @media (prefers-reduced-motion: reduce) { img { transition: none; } }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: -6px;
  width: 300px;
  max-height: calc(100vh - 96px);
  overflow-y: auto;
  padding: 10px;
  background: rgba(16, 18, 26, 0.97);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(91, 157, 217, 0.08);
  backdrop-filter: blur(20px);
  z-index: 1000;
  transform-origin: 22px -8px; /* unfolds from the logo */
  animation: ${unfold} 200ms cubic-bezier(0.22, 1, 0.36, 1);
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Heading = styled.div`
  padding: 6px 10px 10px;
  font-family: 'Rational', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
`;

const GroupLabel = styled.div`
  margin: 8px 10px 4px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
`;

const Notice = styled.div`
  padding: 8px 10px;
  font-size: 0.8rem;
  color: #ffd9d9;
`;

/* One phase: 48 px for touch, no hover state; the current one lit with a blue bar */
const Row = styled.button`
  position: relative;
  width: 100%;
  min-height: 48px;
  padding: 6px 12px 6px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: none;
  border-radius: 10px;
  text-align: left;
  font-family: 'Rational', sans-serif;
  font-size: 0.95rem;
  font-weight: ${(p) => (p.$state === 'current' ? 800 : 600)};
  color: ${(p) => (p.$state === 'current' ? '#fff' : p.$state === 'open' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.32)')};
  background: ${(p) => (p.$state === 'current' ? 'rgba(91, 157, 217, 0.16)' : 'transparent')};
  cursor: ${(p) => (p.$state === 'locked' ? 'default' : 'pointer')};
  -webkit-tap-highlight-color: transparent;
  animation: ${rise} 220ms ease both;
  animation-delay: ${(p) => 40 + p.$i * 30}ms;
  &:active:not(:disabled) { background: rgba(255, 255, 255, 0.08); }
  &:focus-visible { outline: 2px solid ${BLUE}; outline-offset: -2px; }
  &::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 10px;
    bottom: 10px;
    width: 3px;
    border-radius: 2px;
    background: ${(p) => (p.$state === 'current' ? BLUE : 'transparent')};
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/* The intro's phase marks, small: lit for this phase, quiet for an open one, grey when locked */
const Mark = styled.img`
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  opacity: ${(p) => (p.$state === 'current' ? 1 : p.$state === 'open' ? 0.6 : 0.2)};
  filter: ${(p) => (p.$state === 'current' ? 'drop-shadow(0 0 6px rgba(91, 157, 217, 0.55))' : p.$state === 'locked' ? 'grayscale(1)' : 'none')};
`;

const Label = styled.span`
  flex: 1;
  min-width: 0;
  line-height: 1.25;
`;

/**
 * The header's phase menu: the Kuutio logo (and the header's title beside it, which
 * MainViewHeader wires to the same `open`) opens the workshop's phases, in chain order,
 * unfolding from the logo. A phase stays locked until the flow reaches it or the
 * facilitator opens it (#685). Touch first: 44 px button, 48 px rows, no hover states.
 */
export default function HeaderPhaseMenu({
  open, disabled = false, items, currentItem, isSelectable, error = null, onToggle, onSelect, t,
}) {
  return (
    <Anchor>
      <LogoButton
        type="button"
        $open={open}
        disabled={disabled}
        onClick={onToggle}
        aria-label={open ? t('navigation.phasesClose') : t('navigation.phasesOpen')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <img src={LOGO} alt="" />
      </LogoButton>
      {open && !disabled ? (
        <Panel role="menu" aria-label={t('navigation.phasesHeading')}>
          <Heading>{t('navigation.phasesHeading')}</Heading>
          {error ? <Notice>{t('navigation.menuUnavailable', { error })}</Notice> : null}
          {items.map((item, i) => {
            const selectable = isSelectable(item);
            const state = item === currentItem ? 'current' : selectable ? 'open' : 'locked';
            return (
              <React.Fragment key={item.key}>
                {item.isReport ? <GroupLabel>{t('navigation.results')}</GroupLabel> : null}
                <Row
                  type="button"
                  role="menuitem"
                  $state={state}
                  $i={i}
                  aria-current={state === 'current' ? 'page' : undefined}
                  aria-disabled={state === 'locked' || undefined}
                  title={item.unlocked ? undefined : t('navigation.moduleLocked')}
                  onClick={(e) => { e.stopPropagation(); if (selectable) onSelect(item); }}
                >
                  <Mark src={LOGO} alt="" $state={state} />
                  <Label>{item.label}</Label>
                  {state === 'locked' ? <Lock size={14} aria-hidden="true" /> : null}
                </Row>
              </React.Fragment>
            );
          })}
        </Panel>
      ) : null}
    </Anchor>
  );
}
